import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Observable, of } from 'rxjs';
import { catchError, concatMap, map, mapTo, mergeMap, switchMap } from 'rxjs/operators';
import { AuthActions } from '../../../auth';
import { GlobalMessageType } from '../../../global-message';
import { GlobalMessageActions } from '../../../global-message/store/actions';
import { UserSignUp } from '../../../model/misc.model';
import { normalizeHttpError } from '../../../util';
import { UserProfileConnector } from '../../connectors/user-profile.connector';
import { UserActions } from '../actions/index';
import { RegisterActionTypes } from '../actions/register.action';

@Injectable()
export class RegisterEffects {
  registerUser$: Observable<UserActions.RegisterActions> = createEffect(() =>
    this.actions$.pipe(
      ofType(RegisterActionTypes.RegisterUser),
      map((action: UserActions.RegisterUser) => action.payload),
      mergeMap((user: UserSignUp) =>
        this.userConnector.register(user).pipe(
          map((response) => new UserActions.RegisterUserSuccess(response)),
          catchError((error) => of(new UserActions.RegisterUserFail(normalizeHttpError(error))))
        )
      )
    )
  );

  handleFailureForRegisterUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.RegisterActionTypes.RegisterUserFail),
      concatMap((action: UserActions.RegisterUserFail) => {
        const details = action.payload?.details?.filter((detail) => detail.type === 'ValidationError');

        if (details?.length) {
          const addMessages: Array<GlobalMessageActions.AddMessage> = details.map(
            (detail) =>
              new GlobalMessageActions.AddMessage({
                text: { raw: detail.message },
                type: GlobalMessageType.MSG_TYPE_ERROR,
              })
          );
          return addMessages;
        } else {
          return [
            new GlobalMessageActions.AddMessage({
              text: { key: 'registration.signUpFailure_message' },
              type: GlobalMessageType.MSG_TYPE_ERROR,
            }),
          ];
        }
      })
    )
  );

  removeUser$: Observable<UserActions.RegisterActions | AuthActions.Logout> = createEffect(() =>
    this.actions$.pipe(
      ofType(RegisterActionTypes.RemoveUser),
      map((action: UserActions.RemoveUser) => action.payload),
      mergeMap((payload) => {
        return this.userConnector.remove(payload.userId, payload.password).pipe(
          switchMap(() => [new UserActions.RemoveUserSuccess(), new AuthActions.Logout()]),
          catchError((error) => of(new UserActions.RemoveUserFail(normalizeHttpError(error))))
        );
      })
    )
  );

  handleFailureForRemoveUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.RegisterActionTypes.RemoveUserFail),
      mapTo(
        new GlobalMessageActions.AddMessage({
          text: { key: 'settings.account.removeFailure_message' },
          type: GlobalMessageType.MSG_TYPE_ERROR,
        })
      )
    )
  );

  constructor(private actions$: Actions, private userConnector: UserProfileConnector) {}
}
